import { useState, useEffect } from 'react';
import { Palette, Save, Trash2, ArrowLeft, Globe, School, Landmark, Building2, Eye, LayoutGrid, RotateCcw } from 'lucide-react';
import { api } from '../../api/client';
import { toast } from 'sonner';
import {
  BRAND_PROFILE_LABELS,
  DEFAULT_BRAND_BY_PROFILE,
  DEFAULT_BRAND_GRADIENT_BY_PROFILE,
  applyBrandByProfileToDom,
  type BrandProfileKey,
  mergeBrandByProfile,
  mergeBrandGradientByProfile,
  normalizeBrandHex,
  parseHexColor,
} from '../../theme/profileBrandColors';

const BRAND_KEYS = Object.keys(DEFAULT_BRAND_BY_PROFILE) as BrandProfileKey[];

/** Reconstrói atmosfera escura a partir do accent (quando o Master altera a cor). */
function ambientGradientFromAccent(accent: string, fallback: string): string {
  const rgb = parseHexColor(accent);
  if (!rgb) return fallback;
  const stop = (f: number) => {
    const r = Math.round(rgb.r * f);
    const g = Math.round(rgb.g * f);
    const b = Math.round(rgb.b * f);
    return `#${[r, g, b].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
  };
  return `linear-gradient(165deg, ${stop(0.14)} 0%, ${stop(0.38)} 48%, ${stop(0.58)} 100%)`;
}

interface Rede { id: string; nome: string; }
interface Estado { id: string; nome: string; sigla: string; }
interface Municipio { id: string; nome: string; }
interface Escola { id: string; nome: string; }

export function MasterCustomizacaoPage() {
  const [perfil, setPerfil] = useState<'ADMIN' | 'REDE' | 'ESTADUAL' | 'MUNICIPAL' | 'ESCOLA'>('ADMIN');
  const [entidadeId, setEntidadeId] = useState<string>('');

  // Lists for dropdown selectors
  const [redes, setRedes] = useState<Rede[]>([]);
  const [estados, setEstados] = useState<Estado[]>([]);
  const [municipios, setMunicipios] = useState<Municipio[]>([]);
  const [escolas, setEscolas] = useState<Escola[]>([]);
  const [loadingLists, setLoadingLists] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form State
  const [coresLight, setCoresLight] = useState({
    '--primary': '#1e3a8a',
    '--background': '#f8fafc',
    '--card': '#ffffff',
    '--border-color': '#e2e8f0',
    '--text-main': '#0f172a',
  });

  const [coresDark, setCoresDark] = useState({
    '--primary': '#818cf8',
    '--background': '#0f172a',
    '--card': '#1e293b',
    '--border-color': '#334155',
    '--text-main': '#f8fafc',
  });

  const [fontFamily, setFontFamily] = useState('Inter');
  const [borderRadius, setBorderRadius] = useState('8px');
  const [brandByProfile, setBrandByProfile] = useState<Record<BrandProfileKey, string>>(
    () => ({ ...DEFAULT_BRAND_BY_PROFILE }),
  );
  const [brandGradientByProfile, setBrandGradientByProfile] = useState<
    Record<BrandProfileKey, string>
  >(() => ({ ...DEFAULT_BRAND_GRADIENT_BY_PROFILE }));

  const [textos, setTextos] = useState({
    'login.titulo': 'Aluno Conectado',
    'login.subtitulo': 'Programa Escola Segura e Inteligente',
    'dashboard.titulo': 'Painel do Aluno',
  });

  const [assets, setAssets] = useState({
    logoUrl: '',
    faviconUrl: '',
    bgUrl: '',
  });

  // Fetch lists on load
  useEffect(() => {
    const fetchLists = async () => {
      setLoadingLists(true);
      try {
        const [rRedes, rEstados, rMunicipios, rEscolas] = await Promise.all([
          api.get('/admin/redes').catch((err) => { console.warn('[MasterCustomizacaoPage] Erro ao carregar redes:', err); return { data: [] }; }),
          api.get('/admin/estados').catch((err) => { console.warn('[MasterCustomizacaoPage] Erro ao carregar estados:', err); return { data: [] }; }),
          api.get('/admin/municipios').catch((err) => { console.warn('[MasterCustomizacaoPage] Erro ao carregar municípios:', err); return { data: [] }; }),
          api.get('/escolas').catch((err) => { console.warn('[MasterCustomizacaoPage] Erro ao carregar escolas:', err); return { data: [] }; }),
        ]);
        setRedes(Array.isArray(rRedes.data) ? rRedes.data : rRedes.data.data ?? []);
        setEstados(Array.isArray(rEstados.data) ? rEstados.data : rEstados.data.data ?? []);
        setMunicipios(Array.isArray(rMunicipios.data) ? rMunicipios.data : rMunicipios.data.data ?? []);
        
        // As escolas podem vir paginadas ou lista simples
        const escData = rEscolas.data;
        setEscolas(Array.isArray(escData) ? escData : escData.items ?? escData.data ?? []);
      } catch (err) {
        console.warn('Erro ao carregar dados de entidades para customização:', err);
      } finally {
        setLoadingLists(false);
      }
    };
    fetchLists();
  }, []);

  // Fetch specific config when scope changes
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const activeEntId = perfil === 'ADMIN' ? '' : entidadeId;
        if (perfil !== 'ADMIN' && !activeEntId) {
          // Reset form to default if no entity selected
          return;
        }

        const { data } = await api.get('/estilo/config/resolve', {
          params: {
            redeId: perfil === 'REDE' ? activeEntId : undefined,
            estadoId: perfil === 'ESTADUAL' ? activeEntId : undefined,
            municipioId: perfil === 'MUNICIPAL' ? activeEntId : undefined,
            escolaId: perfil === 'ESCOLA' ? activeEntId : undefined,
          }
        });

        if (data) {
          if (data.coresLight) setCoresLight((prev) => ({ ...prev, ...JSON.parse(data.coresLight) }));
          if (data.coresDark) setCoresDark((prev) => ({ ...prev, ...JSON.parse(data.coresDark) }));
          if (data.estilos) {
            const est = JSON.parse(data.estilos);
            if (est.fontFamily) setFontFamily(est.fontFamily);
            if (est.borderRadius) setBorderRadius(est.borderRadius);
            setBrandByProfile(
              mergeBrandByProfile(
                est.brandByProfile && typeof est.brandByProfile === 'object'
                  ? est.brandByProfile
                  : undefined,
              ),
            );
            setBrandGradientByProfile(
              mergeBrandGradientByProfile(
                est.brandGradientByProfile && typeof est.brandGradientByProfile === 'object'
                  ? est.brandGradientByProfile
                  : undefined,
              ),
            );
          } else {
            setBrandByProfile({ ...DEFAULT_BRAND_BY_PROFILE });
            setBrandGradientByProfile({ ...DEFAULT_BRAND_GRADIENT_BY_PROFILE });
          }
          if (data.textos) setTextos((prev) => ({ ...prev, ...JSON.parse(data.textos) }));
          if (data.assets) setAssets((prev) => ({ ...prev, ...JSON.parse(data.assets) }));
        }
      } catch (err) {
        console.warn('Erro ao obter config do estilo:', err);
      }
    };
    fetchConfig();
  }, [perfil, entidadeId]);

  const handleSave = async () => {
    setSaving(true);
    const activeEntId = perfil === 'ADMIN' ? null : entidadeId;
    if (perfil !== 'ADMIN' && !activeEntId) {
      toast.error('Por favor, selecione uma entidade para aplicar as customizações.');
      setSaving(false);
      return;
    }

    try {
      await api.post('/estilo/config', {
        perfil,
        entidadeId: activeEntId,
        coresLight: JSON.stringify(coresLight),
        coresDark: JSON.stringify(coresDark),
        estilos: JSON.stringify({
          fontFamily,
          borderRadius,
          brandByProfile,
          brandGradientByProfile,
        }),
        textos: JSON.stringify(textos),
        assets: JSON.stringify(assets),
      });

      toast.success('Configurações de tema aplicadas com sucesso!');

      // Aplica cores por perfil + pede reload completo do ThemeLoader
      applyBrandByProfileToDom(brandByProfile, brandGradientByProfile);
      window.dispatchEvent(new Event('ac-theme-reload'));
      window.dispatchEvent(new Event('ac-theme-updated'));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao salvar as configurações.');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Deseja realmente limpar as customizações deste nível? (Herdará do nível pai)')) return;
    
    const activeEntId = perfil === 'ADMIN' ? '' : entidadeId;
    try {
      await api.delete('/estilo/config', {
        params: { perfil, entidadeId: activeEntId || undefined }
      });
      toast.success('Customização removida! Revertido para o padrão herdado.');
      setBrandByProfile({ ...DEFAULT_BRAND_BY_PROFILE });
      setBrandGradientByProfile({ ...DEFAULT_BRAND_GRADIENT_BY_PROFILE });
      applyBrandByProfileToDom();
      window.dispatchEvent(new Event('ac-theme-reload'));
      window.dispatchEvent(new Event('ac-theme-updated'));
    } catch (err) {
      console.error(err);
      toast.error('Erro ao limpar customização.');
    }
  };

  const labelStyle: React.CSSProperties = {
    display: 'block', fontSize: '.78rem', fontWeight: 600,
    marginBottom: 6, color: 'var(--text-muted)'
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '8px 12px', borderRadius: 8,
    border: '1px solid var(--border-color)', background: 'var(--bg-card)',
    color: 'var(--text-main)', fontSize: '.85rem'
  };

  return (
    <div className="admin-page animate-fadeIn" style={{ maxWidth: 1000, margin: '0 auto', paddingBottom: 40 }}>
      {/* HERO BANNER */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 40%, #4338ca 100%)',
        borderRadius: 20, padding: '24px 28px', marginBottom: 24, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', top: -40, right: -40, width: 160, height: 160, borderRadius: '50%', background: 'rgba(255,255,255,.05)' }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <Palette size={16} style={{ color: '#818cf8' }} />
            <span style={{ color: 'rgba(255,255,255,.5)', fontSize: '.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.1em' }}>
              Personalização
            </span>
          </div>
          <h1 style={{ color: 'white', fontSize: '1.4rem', fontWeight: 800, margin: 0 }}>Customização Visual & White-labeling</h1>
          <p style={{ color: 'rgba(255,255,255,.6)', fontSize: '.78rem', marginTop: 4 }}>
            Personalize cores, estilos, fontes, textos estáticos e logos para toda a plataforma ou entes específicos com suporte a herança recursiva.
          </p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20 }}>
        {/* CONFIG FORM */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          
          {/* SCOPE PANEL */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 18 }}>
            <h2 style={{ fontSize: '.92rem', fontWeight: 700, margin: '0 0 14px', color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <LayoutGrid size={16} style={{ color: 'var(--primary)' }} /> Escopo da Personalização
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Nível do Perfil</label>
                <select 
                  value={perfil} 
                  onChange={(e) => { setPerfil(e.target.value as any); setEntidadeId(''); }}
                  style={inputStyle}
                >
                  <option value="ADMIN">Master Global (Padrão Principal)</option>
                  <option value="REDE">Rede Escolar</option>
                  <option value="ESTADUAL">Estado</option>
                  <option value="MUNICIPAL">Município</option>
                  <option value="ESCOLA">Escola Individual</option>
                </select>
              </div>

              {perfil !== 'ADMIN' && (
                <div>
                  <label style={labelStyle}>Selecionar Ente / Vínculo</label>
                  <select 
                    value={entidadeId} 
                    onChange={(e) => setEntidadeId(e.target.value)}
                    style={inputStyle}
                    disabled={loadingLists}
                  >
                    <option value="">-- Selecione uma entidade --</option>
                    {perfil === 'REDE' && redes.map(r => <option key={r.id} value={r.id}>{r.nome}</option>)}
                    {perfil === 'ESTADUAL' && estados.map(e => <option key={e.id} value={e.id}>{e.nome} ({e.sigla})</option>)}
                    {perfil === 'MUNICIPAL' && municipios.map(m => <option key={m.id} value={m.id}>{m.nome}</option>)}
                    {perfil === 'ESCOLA' && escolas.map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
                  </select>
                </div>
              )}
            </div>
          </div>

          {/* PALETTES PANEL */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 18 }}>
            <h2 style={{ fontSize: '.92rem', fontWeight: 700, margin: '0 0 16px', color: 'var(--text-main)' }}>
              Paleta de Cores (Light & Dark)
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              {/* LIGHT THEME */}
              <div style={{ borderRight: '1px solid var(--border-color)', paddingRight: 20 }}>
                <h3 style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  ☀️ Modo Claro (Light)
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { key: '--primary', label: 'Cor Primária' },
                    { key: '--background', label: 'Cor de Fundo' },
                    { key: '--card', label: 'Cor dos Cards' },
                    { key: '--border-color', label: 'Cor das Bordas' },
                    { key: '--text-main', label: 'Cor do Texto' },
                  ].map(item => (
                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>{item.label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '.72rem', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                          {coresLight[item.key as keyof typeof coresLight]}
                        </span>
                        <input 
                          type="color" 
                          value={coresLight[item.key as keyof typeof coresLight]} 
                          onChange={(e) => setCoresLight({ ...coresLight, [item.key]: e.target.value })}
                          style={{ width: 28, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* DARK THEME */}
              <div>
                <h3 style={{ fontSize: '.8rem', fontWeight: 700, color: 'var(--text-main)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                  🌙 Modo Escuro (Dark)
                </h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {[
                    { key: '--primary', label: 'Cor Primária' },
                    { key: '--background', label: 'Cor de Fundo' },
                    { key: '--card', label: 'Cor dos Cards' },
                    { key: '--border-color', label: 'Cor das Bordas' },
                    { key: '--text-main', label: 'Cor do Texto' },
                  ].map(item => (
                    <div key={item.key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                      <span style={{ fontSize: '.76rem', color: 'var(--text-muted)' }}>{item.label}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={{ fontSize: '.72rem', fontFamily: 'monospace', color: 'var(--text-muted)' }}>
                          {coresDark[item.key as keyof typeof coresDark]}
                        </span>
                        <input 
                          type="color" 
                          value={coresDark[item.key as keyof typeof coresDark]} 
                          onChange={(e) => setCoresDark({ ...coresDark, [item.key]: e.target.value })}
                          style={{ width: 28, height: 28, border: 'none', borderRadius: 4, cursor: 'pointer', background: 'transparent' }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* CORES POR PERFIL (sidebar + desktop) */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 18 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 14 }}>
              <div>
                <h2 style={{ fontSize: '.92rem', fontWeight: 700, margin: 0, color: 'var(--text-main)' }}>
                  Cores por perfil
                </h2>
                <p style={{ fontSize: '.72rem', color: 'var(--text-muted)', margin: '4px 0 0' }}>
                  Mesma atmosfera dos ambientes do desktop: accent saturado + gradiente escuro na sidebar.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setBrandByProfile({ ...DEFAULT_BRAND_BY_PROFILE });
                  setBrandGradientByProfile({ ...DEFAULT_BRAND_GRADIENT_BY_PROFILE });
                }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  height: 30,
                  padding: '0 10px',
                  borderRadius: 8,
                  border: '1px solid var(--border-color)',
                  background: 'var(--bg-main, #fff)',
                  color: 'var(--text-muted)',
                  fontSize: '.72rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
                title="Restaurar padrões do desktop"
              >
                <RotateCcw size={12} /> Padrão
              </button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {BRAND_KEYS.map((key) => (
                <div
                  key={key}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 10,
                    padding: '8px 10px',
                    borderRadius: 10,
                    border: '1px solid var(--border-color)',
                    background: brandGradientByProfile[key],
                    color: '#fff',
                  }}
                >
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: '.76rem', fontWeight: 700 }}>
                      {BRAND_PROFILE_LABELS[key]}
                    </div>
                    <div style={{ fontSize: '.68rem', fontFamily: 'monospace', opacity: 0.75 }}>
                      {brandByProfile[key]}
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: brandByProfile[key],
                        border: '1px solid rgba(255,255,255,.35)',
                        boxShadow: `0 0 10px ${brandByProfile[key]}88`,
                        flexShrink: 0,
                      }}
                    />
                    <input
                      type="color"
                      value={normalizeBrandHex(brandByProfile[key], DEFAULT_BRAND_BY_PROFILE[key])}
                      onChange={(e) => {
                        const next = normalizeBrandHex(e.target.value, brandByProfile[key]);
                        setBrandByProfile((prev) => ({ ...prev, [key]: next }));
                        setBrandGradientByProfile((prev) => ({
                          ...prev,
                          [key]: ambientGradientFromAccent(
                            next,
                            DEFAULT_BRAND_GRADIENT_BY_PROFILE[key],
                          ),
                        }));
                      }}
                      style={{
                        width: 28,
                        height: 28,
                        border: 'none',
                        borderRadius: 4,
                        cursor: 'pointer',
                        background: 'transparent',
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* FONT & LAYOUT */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 18 }}>
            <h2 style={{ fontSize: '.92rem', fontWeight: 700, margin: '0 0 14px', color: 'var(--text-main)' }}>
              Tipografia & Bordas
            </h2>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Fonte de Texto (Google Fonts)</label>
                <select value={fontFamily} onChange={(e) => setFontFamily(e.target.value)} style={inputStyle}>
                  <option value="Inter">Inter (Padrão)</option>
                  <option value="Outfit">Outfit (Moderna)</option>
                  <option value="Outfit, sans-serif">Outfit & Sans</option>
                  <option value="Roboto">Roboto (Clássica)</option>
                  <option value="system-ui">Sistema Nativo</option>
                </select>
              </div>

              <div>
                <label style={labelStyle}>Arredondamento dos Controles (Border Radius)</label>
                <select value={borderRadius} onChange={(e) => setBorderRadius(e.target.value)} style={inputStyle}>
                  <option value="4px">4px (Compacto / Reto)</option>
                  <option value="8px">8px (Padrão Suave)</option>
                  <option value="12px">12px (Premium Arredondado)</option>
                  <option value="16px">16px (Extremamente Arredondado)</option>
                </select>
              </div>
            </div>
          </div>

          {/* STATIC TEXTS */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 18 }}>
            <h2 style={{ fontSize: '.92rem', fontWeight: 700, margin: '0 0 14px', color: 'var(--text-main)' }}>
              Textos Estáticos Personalizados
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={labelStyle}>Título na Tela de Login</label>
                <input 
                  value={textos['login.titulo']} 
                  onChange={(e) => setTextos({ ...textos, 'login.titulo': e.target.value })}
                  style={inputStyle}
                  placeholder="Ex: Aluno Conectado"
                />
              </div>
              <div>
                <label style={labelStyle}>Subtítulo na Tela de Login</label>
                <input 
                  value={textos['login.subtitulo']} 
                  onChange={(e) => setTextos({ ...textos, 'login.subtitulo': e.target.value })}
                  style={inputStyle}
                  placeholder="Ex: Programa Escola Segura"
                />
              </div>
            </div>
          </div>

          {/* ASSETS */}
          <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-color)', borderRadius: 14, padding: 18 }}>
            <h2 style={{ fontSize: '.92rem', fontWeight: 700, margin: '0 0 14px', color: 'var(--text-main)' }}>
              Logotipos e Imagens
            </h2>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={labelStyle}>URL do Logotipo Principal (PNG / SVG)</label>
                <input 
                  value={assets.logoUrl} 
                  onChange={(e) => setAssets({ ...assets, logoUrl: e.target.value })}
                  style={inputStyle}
                  placeholder="https://exemplo.com/imagens/logo.png"
                />
              </div>
              <div>
                <label style={labelStyle}>URL do Favicon (.ico / .png)</label>
                <input 
                  value={assets.faviconUrl} 
                  onChange={(e) => setAssets({ ...assets, faviconUrl: e.target.value })}
                  style={inputStyle}
                  placeholder="https://exemplo.com/imagens/favicon.ico"
                />
              </div>
            </div>
          </div>

          {/* ACTION BUTTONS */}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 10 }}>
            <button 
              onClick={handleReset}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 9,
                background: 'none', border: '1px solid #ef4444', color: '#ef4444', fontWeight: 700,
                fontSize: '.82rem', cursor: 'pointer', transition: 'all .2s'
              }}
            >
              <Trash2 size={14} /> Limpar Customização
            </button>
            
            <button 
              onClick={handleSave}
              disabled={saving}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, padding: '10px 24px', borderRadius: 9,
                background: 'var(--primary)', color: 'white', fontWeight: 700, border: 'none',
                fontSize: '.82rem', cursor: 'pointer', opacity: saving ? 0.7 : 1, transition: 'all .2s'
              }}
            >
              <Save size={14} /> {saving ? 'Salvando...' : 'Aplicar e Salvar'}
            </button>
          </div>

        </div>

        {/* SIDEBAR PREVIEW */}
        <div>
          <div style={{
            position: 'sticky', top: 20, background: 'var(--bg-card)',
            border: '1px solid var(--border-color)', borderRadius: 14, padding: 16,
            display: 'flex', flexDirection: 'column', gap: 14
          }}>
            <h2 style={{ fontSize: '.88rem', fontWeight: 700, margin: 0, color: 'var(--text-main)', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Eye size={15} /> Pré-visualização
            </h2>

            <div style={{ 
              border: '1px solid var(--border-color)', borderRadius: 10, overflow: 'hidden',
              background: coresLight['--background'], height: 260, position: 'relative',
              display: 'flex', flexDirection: 'column'
            }}>
              {/* Fake TopBar */}
              <div style={{
                height: 38, background: coresLight['--card'], borderBottom: `1px solid ${coresLight['--border-color']}`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444' }} />
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#fbbf24' }} />
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981' }} />
                </div>
                <div style={{ fontSize: '.6rem', color: coresLight['--text-main'], opacity: 0.7, fontWeight: 700 }}>
                  {textos['login.titulo']}
                </div>
              </div>

              {/* Fake Content */}
              <div style={{ flex: 1, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{
                  height: 34, background: coresLight['--card'], border: `1px solid ${coresLight['--border-color']}`,
                  borderRadius: borderRadius, display: 'flex', alignItems: 'center', padding: '0 8px', gap: 6
                }}>
                  <div style={{ width: 14, height: 14, borderRadius: '50%', background: coresLight['--primary'] }} />
                  <div style={{ height: 6, width: 80, background: coresLight['--text-main'], opacity: 0.2, borderRadius: 3 }} />
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  <div style={{
                    height: 50, background: coresLight['--card'], border: `1px solid ${coresLight['--border-color']}`,
                    borderRadius: borderRadius, padding: 8, display: 'flex', flexDirection: 'column', gap: 4
                  }}>
                    <div style={{ height: 10, width: 24, background: coresLight['--primary'], borderRadius: 2 }} />
                    <div style={{ height: 5, width: 44, background: coresLight['--text-main'], opacity: 0.15, borderRadius: 2 }} />
                  </div>
                  <div style={{
                    height: 50, background: coresLight['--card'], border: `1px solid ${coresLight['--border-color']}`,
                    borderRadius: borderRadius, padding: 8, display: 'flex', flexDirection: 'column', gap: 4
                  }}>
                    <div style={{ height: 10, width: 34, background: '#10b981', borderRadius: 2 }} />
                    <div style={{ height: 5, width: 38, background: coresLight['--text-main'], opacity: 0.15, borderRadius: 2 }} />
                  </div>
                </div>

                <button style={{
                  height: 28, border: 'none', background: coresLight['--primary'], color: 'white',
                  borderRadius: borderRadius, fontSize: '.68rem', fontWeight: 700, cursor: 'pointer', marginTop: 'auto'
                }}>
                  Botão Ação
                </button>
              </div>
            </div>

            <div style={{ fontSize: '.7rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
              * Esta pré-visualização simula o layout no <strong>Modo Claro</strong> usando as variáveis modificadas de arredondamento e cores.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
